import { RAG as RAG_LEVELS } from 'features/dashboard/charts/scoreColour'

export const LEGISLATION_TYPE_META = {
  act:           { label: 'Act', fg: '#3730a3', bg: '#eef2ff', desc: 'Primary legislation. An Act of Parliament. Legally binding.' },
  regulation:    { label: 'Regulation / Order', fg: '#6d28d9', bg: '#f5f3ff', desc: 'Secondary legislation. Regulations or an order made under an Act. Legally binding.' },
  acop:          { label: 'Approved Code', fg: '#33becc', bg: '#e6f7f9', desc: 'Approved Code of Practice. Special legal status; following it is normally enough to comply with the law.' },
  standard:      { label: 'Standard', fg: '#334155', bg: '#f1f5f9', desc: 'British / European standard (BS, EN, ISO, PAS). Recognised good practice; not law unless a regulation cites it.' },
  guidance:      { label: 'Guidance', fg: '#0e7490', bg: '#ecfeff', desc: 'Official or industry guidance (e.g. HSE). Strongly advised and supports compliance, but not itself law.' },
  best_practice: { label: 'Best practice', fg: '#64748b', bg: '#f1f5f9', desc: 'Industry or manufacturer best practice. Recommended to reduce risk; not a legal requirement.' },
  other:         { label: 'Other', fg: '#64748b', bg: '#f1f5f9', desc: 'Origin not classified.' },
}

export const legislationTypeMeta = key => LEGISLATION_TYPE_META[key] || LEGISLATION_TYPE_META.other

export const CADENCE_PRESETS = [
  { label: 'Daily',       days: 1 },
  { label: 'Weekly',      days: 7 },
  { label: 'Monthly',     days: 30 },
  { label: 'Quarterly',   days: 90 },
  { label: '6-monthly',   days: 182 },
  { label: 'Annual',      days: 365 },
  { label: '2-yearly',    days: 730 },
  { label: '5-yearly',    days: 1825 },
  { label: '10-yearly',   days: 3650 },
  { label: 'One-off',     days: null },
]

export const CRITICALITY_LEVELS = [
  { label: 'Low',      value: 1, key: 'low',      colour: RAG_LEVELS.low.fg },
  { label: 'Medium',   value: 2, key: 'medium',   colour: RAG_LEVELS.medium.fg },
  { label: 'High',     value: 3, key: 'high',     colour: RAG_LEVELS.high.fg },
  { label: 'Critical', value: 4, key: 'critical', colour: RAG_LEVELS.critical.fg },
]

export const PENALTY_SEVERITIES = {
  none:           { label: 'No specific penalty', colour: RAG_LEVELS.none.fg,     desc: 'No legal penalty. An internal, contractual or best-practice duty.' },
  civil:          { label: 'Civil penalty',       colour: RAG_LEVELS.low.fg,      desc: 'Enforced through civil action or an improvement notice, not a criminal charge.' },
  fine:           { label: 'Fine',                colour: RAG_LEVELS.medium.fg,   desc: 'Breaching it is a criminal offence carrying a set or limited fine.' },
  unlimited_fine: { label: 'Unlimited fine',      colour: RAG_LEVELS.high.fg,     desc: 'A serious criminal offence. A court can impose an unlimited fine. Common for fire, gas, electrical and building-safety duties.' },
  imprisonment:   { label: 'Imprisonment',        colour: RAG_LEVELS.critical.fg, desc: 'The gravest. Breaching it can lead to prosecution and a prison sentence for the responsible person.' },
}

export const RAG = {
  green: { key: 'green', label: 'Green', colour: RAG_LEVELS.low.fg },
  amber: { key: 'amber', label: 'Amber', colour: RAG_LEVELS.medium.fg },
  red:   { key: 'red',   label: 'Red',   colour: RAG_LEVELS.critical.fg },
}

export const ragForStatus = status => {
  if (status === 'skipped') return null
  if (status === 'satisfied') return RAG.green
  if (status === 'expired') return RAG.red
  return RAG.amber
}

export const SCOPE_OPTIONS = [
  { label: 'Whole building', value: 'building' },
  { label: 'Per unit',       value: 'unit' },
]

export const closestCadencePreset = days => {
  if (days === null || days === undefined) {
    return CADENCE_PRESETS.find(p => p.days === null)
  }
  let best = null; let diff = Infinity
  for (const p of CADENCE_PRESETS) {
    if (p.days === null) continue
    const d = Math.abs(p.days - days)
    if (d < diff) { diff = d; best = p }
  }
  return best
}

export const cadenceLabel = days => {
  if (days === null || days === undefined || days <= 0) return 'Not scheduled'
  const p = closestCadencePreset(days)
  if (p && p.days !== null && Math.abs(p.days - days) < 3) return p.label
  if (days % 365 === 0) { const y = days / 365; return y === 1 ? 'Annual' : `Every ${y} years` }
  if (days % 30 === 0) return `Every ${days / 30} months`
  if (days % 7 === 0) return `Every ${days / 7} weeks`
  return `Every ${days} days`
}

export const criticalityFromWeight = weight => {
  const w = Number(weight) || 1
  if (w >= 4) return CRITICALITY_LEVELS[3]
  if (w === 3) return CRITICALITY_LEVELS[2]
  if (w === 2) return CRITICALITY_LEVELS[1]
  return CRITICALITY_LEVELS[0]
}

export const criticalityLabel = weight => criticalityFromWeight(weight).label

export const criticalityTint = weight => criticalityFromWeight(weight).colour
